import { getUserDisplayName } from "../lib/userDisplayName";
import "../styles/home-widgets.css";
import { HomeHero } from "../components/HomeHero";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Building2, Calendar, UtensilsCrossed, Flame, Users, MessageSquareText, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { apiClient, type CommunityHighlightsResponse, type CommunityPostListItem } from "../lib/api";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { usePageSEO } from "../lib/usePageSEO";
import { formatYmdInSeoul } from "../lib/koreaDate";
import { SchoolOverviewCard } from "../components/SchoolOverviewCard";
import { QuickFavorites } from "../components/QuickFavorites";

function CommunityHighlightsSection() {
  const { isAuthenticated } = useAuth();
  const [highlights, setHighlights] = useState<CommunityHighlightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await apiClient.getCommunityHighlights();
        if (!mounted) return;
        setHighlights(data);
        setError(null);
      } catch (e: any) {
        if (!mounted) return;
        console.error("Failed to load community highlights:", e);
        setError("소통공간 글을 불러오는 중 문제가 발생했습니다.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const renderList = (
    posts: CommunityPostListItem[],
    rightType: "comments" | "author",
  ) => {
    if (!posts.length) {
      return (
        <p className="text-sm text-gray-500">
          아직 표시할 글이 없습니다.
        </p>
      );
    }

    return (
      <ul className="space-y-1.5">
        {posts.map((post) => {
          const rightText =
            rightType === "comments"
              ? `(${post.num_comments})`
              : `(${getUserDisplayName(post.author)})`;

          return (
            <li key={post.id}>
              <Link
                to={`/community/board/${post.id}`}
                className="fe-home-feed-row group block rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate whitespace-nowrap group-hover:text-[#536541]">
                    {post.title}
                  </span>
                  <span className="fe-home-feed-meta ml-2 flex-shrink-0 text-xs text-gray-500 text-right">
                    {rightText}
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  };

  const latest = (highlights?.latest ?? []).slice().sort((a, b) => {
    const tb = new Date(b.created_at).getTime();
    const ta = new Date(a.created_at).getTime();
    const nb = Number.isFinite(tb);
    const na = Number.isFinite(ta);
    if (nb && na && tb !== ta) return tb - ta;
    if (nb && !na) return -1;
    if (!nb && na) return 1;
    return b.id - a.id;
  });
  const popular = highlights?.popular ?? [];
  const following = highlights?.following ?? [];

  return (
    <section className="fe-home-community py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="fe-section-heading"><div><span className="fe-eyebrow">BETTER TOGETHER</span><h2>함께 나누면, 더 좋은 하루</h2></div><Link to="/community" className="fe-text-link">소통공간 가기 <ArrowRight size={16} /></Link></div>
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="fe-surface rounded-3xl bg-white/80 border border-gray-100 shadow-sm p-5 animate-pulse"
              >
                <div className="h-4 w-28 bg-gray-200 rounded-full mb-2" />
                <div className="h-3 w-20 bg-gray-200 rounded-full mb-4" />
                <div className="space-y-2">
                  <div className="h-3 w-full bg-gray-100 rounded" />
                  <div className="h-3 w-5/6 bg-gray-100 rounded" />
                  <div className="h-3 w-4/6 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div role="alert" className="rounded-2xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 최신 글 */}
            <div className="fe-home-feed-card fe-surface rounded-3xl bg-white/90 border border-gray-100 shadow-sm p-5 flex flex-col">
              <div className="fe-home-feed-heading flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="fe-home-icon fe-home-icon--lavender">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      최신 글 보기
                    </h3>
                    <p className="text-xs text-gray-500">
                      방금 올라온 글부터 차례대로
                    </p>
                  </div>
                </div>
                <Link
                  to="/community/board"
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                >
                  더 보기
                </Link>
              </div>
              <div className="flex-1 min-h-[200px]">
                {renderList(latest, "comments")}
              </div>
            </div>

            {/* 인기 글 */}
            <div className="fe-home-feed-card fe-surface rounded-3xl bg-white/90 border border-gray-100 shadow-sm p-5 flex flex-col">
              <div className="fe-home-feed-heading flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="fe-home-icon fe-home-icon--peach">
                    <Flame className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      인기글 보기
                    </h3>
                    <p className="text-xs text-gray-500">
                      조회, 좋아요, 댓글이 많은 글
                    </p>
                  </div>
                </div>
                <Link
                  to="/community/board"
                  className="text-xs font-medium text-amber-700 hover:text-amber-800 hover:underline"
                >
                  더 보기
                </Link>
              </div>
              <div className="flex-1 min-h-[200px]">
                {renderList(popular, "comments")}
              </div>
            </div>

            {/* 팔로잉 선생님 글 */}
            <div className="fe-home-feed-card fe-surface rounded-3xl bg-white/90 border border-gray-100 shadow-sm p-5 flex flex-col">
              <div className="fe-home-feed-heading flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="fe-home-icon fe-home-icon--sage">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      팔로잉 선생님 글 보기
                    </h3>
                    <p className="text-xs text-gray-500">
                      내가 팔로우한 선생님의 최신 10개 글
                    </p>
                  </div>
                </div>
                <Link
                  to="/community/board"
                  className="text-xs font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                >
                  더 보기
                </Link>
              </div>
              <div className="flex-1 min-h-[200px]">
                {following.length ? (
                  renderList(following, "author")
                ) : (
                  <div className="h-full flex flex-col items-start justify-center gap-3 text-sm text-gray-600">
                    {!isAuthenticated ? (
                      <>
                        <p>로그인하면 팔로잉한 선생님 글을 모아서 볼 수 있어요.</p>
                        <Link
                          to="/login"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-800 transition-colors"
                        >
                          로그인하고 팔로우하러 가기
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-600">
                          아직 팔로잉한 선생님이 없습니다.
                        </p>
                        <p className="text-xs text-gray-500">
                          소통공간에서 마음에 드는 선생님을 팔로우하면,
                          이곳에서 그분들의 글을 한 번에 모아볼 수 있어요.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Link
                            to="/community/board"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors"
                          >
                            소통공간 둘러보기
                            <ArrowRight className="w-3 h-3" />
                          </Link>
                          <Link
                            to="/follows?tab=following"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-emerald-700 border border-emerald-100 text-xs font-medium hover:bg-emerald-50 transition-colors"
                          >
                            팔로우 관리하기
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function formatScheduleDayLabel(ymd: string) {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  const weekday = ["일", "월", "화", "수", "목", "금", "토"][new Date(y, m - 1, d).getDay()];
  return `${y}년 ${m}월 ${d}일 (${weekday})`;
}

function collectCalendarEventsForDay(calendar: any, dateStr: string) {
  if (!calendar || !dateStr) return [] as Array<{ title: string; start: string }>;
  return calendar
    .getEvents()
    .filter((ev: any) => String(ev.startStr || "").slice(0, 10) === dateStr)
    .map((ev: any) => ({ title: String(ev.title || ""), start: String(ev.startStr || dateStr) }));
}

function SchoolInfoHero() {
  const { isAuthenticated } = useAuth();
  const [schoolInfo, setSchoolInfo] = useState<any>(null);
  const [mealInfo, setMealInfo] = useState<{ mealTypes: string[]; meals: Record<string, string[] | string>; date: string } | null>(null);
  const [selectedMealType, setSelectedMealType] = useState<string>('');
  const [mealDate, setMealDate] = useState<string>(formatYmdInSeoul());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const calendarInstanceRef = useRef<any>(null);
  const [selectedScheduleDay, setSelectedScheduleDay] = useState<{
    date: string;
    events: Array<{ title: string; start: string }>;
  } | null>(null);

  // FullCalendar 스크립트 로드
  useEffect(() => {
    if (!isAuthenticated || !schoolInfo) return;

    const loadFullCalendar = () => {
      if ((window as any).FullCalendar) {
        setCalendarLoaded(true);
        return;
      }

      // CSS 로드
      const existingCSS = document.querySelector('link[href*="fullcalendar"]');
      if (!existingCSS) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/npm/fullcalendar@6.1.8/index.global.min.css';
        document.head.appendChild(link);
      }

      // JS 로드
      const existingScript = document.querySelector('script[src*="fullcalendar"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => setCalendarLoaded(true));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/fullcalendar@6.1.8/index.global.min.js';
      script.async = true;
      script.onload = () => setCalendarLoaded(true);
      document.head.appendChild(script);
    };

    loadFullCalendar();
  }, [isAuthenticated, schoolInfo]);

  useEffect(() => {
    if (!selectedScheduleDay) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedScheduleDay(null);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedScheduleDay]);

  // 캘린더 초기화
  useEffect(() => {
    if (!calendarLoaded || !calendarRef.current || !schoolInfo) return;

    const FullCalendar = (window as any).FullCalendar;
    if (!FullCalendar) return;

    // 기존 캘린더 제거
    if (calendarInstanceRef.current) {
      calendarInstanceRef.current.destroy();
    }

    const openDayDetail = (dateStr: string, calendarApi: any) => {
      const events = collectCalendarEventsForDay(calendarApi, dateStr);
      setSelectedScheduleDay({ date: dateStr, events });
    };

    const calendarEl = calendarRef.current;
    const calendar = new FullCalendar.Calendar(calendarEl, {
      initialView: 'dayGridMonth',
      height: 'auto',
      aspectRatio: window.matchMedia('(max-width: 640px)').matches ? 1.05 : 1.35,
      headerToolbar: {
        left: 'prev,next',
        center: 'title',
        right: ''
      },
      locale: 'ko',
      firstDay: 0, // 일요일부터 시작
      dayMaxEvents: 2,
      moreLinkClick: 'popover',
      // 날짜 숫자만 표시해 좁은 칸에서 "1일" 세로 깨짐을 방지
      dayCellContent: (arg: any) => ({
        html: `<span class="fc-day-num">${String(arg.dayNumberText || "").replace(/일/g, "")}</span>`,
      }),
      dateClick: (info: any) => {
        openDayDetail(info.dateStr, info.view.calendar);
      },
      eventClick: (info: any) => {
        info.jsEvent.preventDefault();
        const dateStr = String(info.event.startStr || "").slice(0, 10);
        if (dateStr) openDayDetail(dateStr, info.view.calendar);
      },
      datesSet: () => {
        setSelectedScheduleDay(null);
      },
      events: async (info: any, successCallback: any, failureCallback: any) => {
        try {
          // FullCalendar의 startStr과 endStr은 YYYY-MM-DD 형식
          // 이미 시간이 포함되어 있는지 확인
          let start = info.startStr;
          let end = info.endStr;

          // YYYY-MM-DD 형식인지 확인 (시간이 없으면 추가)
          if (!start.includes('T')) {
            start = `${start}T00:00:00+09:00`;
          }
          if (!end.includes('T')) {
            end = `${end}T23:59:59+09:00`;
          }

          let atptCode = schoolInfo?.ATPT_OFCDC_SC_CODE || schoolInfo?.atpt_ofcdc_sc_code;
          let sdCode = schoolInfo?.SD_SCHUL_CODE || schoolInfo?.sd_schul_code;

          if (!atptCode || !sdCode) {
            const fetchedSchool = await apiClient.getSchoolInfo().catch(() => null);
            if (fetchedSchool) {
              atptCode = fetchedSchool.ATPT_OFCDC_SC_CODE || fetchedSchool.atpt_ofcdc_sc_code;
              sdCode = fetchedSchool.SD_SCHUL_CODE || fetchedSchool.sd_schul_code;
            }
          }

          if (!atptCode || !sdCode) {
            failureCallback();
            return;
          }

          const events = await apiClient.getSchoolSchedule(start, end, atptCode, sdCode);
          successCallback(events);
        } catch (error) {
          console.error('학사일정 로드 실패:', error);
          failureCallback();
        }
      },
      eventDisplay: 'list-item',
      eventColor: '#7c8d65',
      eventTextColor: '#ffffff',
      dayHeaderFormat: { weekday: 'short' }, // 요일을 짧게 표시 (월, 화, 수...)
      titleFormat: { year: 'numeric', month: 'long' }, // 제목 형식
      buttonText: {
        today: '오늘',
        month: '월',
        week: '주',
        day: '일'
      },
    });

    calendar.render();
    calendarInstanceRef.current = calendar;

    return () => {
      if (calendarInstanceRef.current) {
        calendarInstanceRef.current.destroy();
        calendarInstanceRef.current = null;
      }
    };
  }, [calendarLoaded, schoolInfo]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      const isInitialLoad = !schoolInfo;
      try {
        if (isInitialLoad) setLoading(true);
        let school = schoolInfo;
        if (!school) {
          school = await apiClient.getSchoolInfo().catch(() => null);
        }

        const atptCode = school?.ATPT_OFCDC_SC_CODE || school?.atpt_ofcdc_sc_code;
        const sdCode = school?.SD_SCHUL_CODE || school?.sd_schul_code;

        let meal = null;
        if (school && atptCode && sdCode) {
          meal = await apiClient.getSchoolMeal(mealDate, atptCode, sdCode).catch(() => null);
        }

        // Only update if it's the first time we got school info or if it changed
        if (school && !schoolInfo) {
          setSchoolInfo(school);
        }
        if (meal) {
          setMealInfo(meal);
          // 중식이 있으면 중식 선택, 없으면 첫 번째 타입 선택
          const defaultType = meal.mealTypes.includes('중식') ? '중식' : meal.mealTypes[0];
          setSelectedMealType(defaultType || '');
        }
        setError(null);
      } catch (err: any) {
        setError(err.message || '정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        if (isInitialLoad) setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, mealDate]);

  const handleMealDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMealDate(e.target.value);
  };

  const handleMealTypeClick = (type: string) => {
    setSelectedMealType(type);
  };

  if (!isAuthenticated) {
    return (
      <section className="fe-school-section py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="fe-surface bg-white rounded-3xl border border-gray-200 shadow-lg p-8 lg:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="fe-home-icon fe-home-icon--sage">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">학교 등록</h2>
                <p className="text-gray-600">로그인 후 학교 정보를 등록하세요!</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              로그인 후 학교 정보를 등록하면 시간표와 급식 정보를 확인할 수 있습니다.
            </p>
            <Link
              to="/login"
              className="fe-primary-action inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              로그인하기
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="fe-school-section py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">정보를 불러오는 중...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && !schoolInfo) {
    return (
      <section className="fe-school-section py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="fe-surface bg-white rounded-3xl border border-gray-200 shadow-lg p-8 lg:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="fe-home-icon fe-home-icon--sage">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">학교 등록</h2>
                <p className="text-gray-600">학교 정보를 등록하세요!</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              학교 정보를 등록하면 시간표와 급식 정보를 확인할 수 있습니다.
            </p>
            <a
              href="https://www.foreducator.com/account/profile"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              학교 등록하기 (프로필 수정)
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    );
  }

  if (!schoolInfo) {
    return (
      <section className="fe-school-section py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="fe-surface bg-white rounded-3xl border border-gray-200 shadow-lg p-8 lg:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="fe-home-icon fe-home-icon--sage">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">학교 등록</h2>
                <p className="text-gray-600">학교 정보를 등록하세요!</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              학교 정보를 등록하면 시간표와 급식 정보를 확인할 수 있습니다.
            </p>
            <a
              href="https://www.foreducator.com/account/profile"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              학교 등록하기 (프로필 수정)
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="fe-school-section py-10 sm:py-12">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-4 sm:gap-6">
          {/* 학교 정보 카드 */}
          <div className="lg:col-span-3 min-w-0">
            <SchoolOverviewCard school={schoolInfo} />
          </div>

          {/* 캘린더 카드 */}
          <div className="lg:col-span-5">
            <div className="fe-surface bg-white rounded-3xl border border-gray-200 shadow-lg p-3 sm:p-6 h-full flex flex-col">
              <div className="flex items-center gap-3 mb-1 sm:mb-2">
                <div className="fe-home-icon fe-home-icon--lavender">
                  <Calendar className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-gray-900">학사일정</h3>
                  <p className="text-xs text-gray-500 mt-0.5">날짜를 눌러 일정을 확인하세요</p>
                </div>
              </div>
              <div className="flex-1 min-h-[280px]">
                {calendarLoaded ? (
                  <>
                    <style>{`
                      .compact-calendar .fc-header-toolbar {
                        margin-bottom: 0.5rem !important;
                        padding-bottom: 0.35rem;
                      }
                      .compact-calendar .fc-toolbar-title {
                        font-size: 0.875rem !important;
                        font-weight: 600;
                      }
                      .compact-calendar .fc-button {
                        font-size: 0.75rem !important;
                        padding: 0.25rem 0.5rem !important;
                        height: auto !important;
                        line-height: 1.2 !important;
                      }
                      .compact-calendar .fc-col-header-cell {
                        font-size: 0.7rem !important;
                        padding: 0.375rem 0.125rem !important;
                        font-weight: 600;
                      }
                      .compact-calendar .fc-daygrid-day {
                        font-size: 0.7rem !important;
                        cursor: pointer;
                      }
                      .compact-calendar .fc-daygrid-day:hover {
                        background-color: #f8fafc;
                      }
                      .compact-calendar .fc-daygrid-day-number {
                        font-size: 0.75rem !important;
                        padding: 0.2rem 0.25rem !important;
                        white-space: nowrap !important;
                      }
                      .compact-calendar .fc-day-num {
                        display: inline-block;
                        line-height: 1.2;
                      }
                      .compact-calendar .fc-event {
                        font-size: 0.65rem !important;
                        padding: 0.125rem 0.25rem !important;
                        margin: 0.125rem 0 !important;
                        line-height: 1.2 !important;
                        border-radius: 0.25rem;
                        cursor: pointer;
                      }
                      .compact-calendar .fc-daygrid-day-frame {
                        min-height: 2.5rem !important;
                        padding: 0.125rem;
                      }
                      .compact-calendar .fc-daygrid-day-top {
                        flex-direction: row;
                        justify-content: flex-start;
                      }
                      .compact-calendar .fc-scrollgrid {
                        border-width: 1px;
                      }
                      .compact-calendar .fc-scrollgrid-section-header td {
                        border-width: 1px;
                      }
                      /* 모바일: 칸이 좁아 제목이 잘리므로 점만 남기고 클릭으로 상세 확인 */
                      @media (max-width: 640px) {
                        .compact-calendar .fc-toolbar-title {
                          font-size: 0.8rem !important;
                        }
                        .compact-calendar .fc-col-header-cell {
                          font-size: 0.65rem !important;
                          padding: 0.25rem 0 !important;
                        }
                        .compact-calendar .fc-daygrid-day-number {
                          font-size: 0.7rem !important;
                          padding: 0.15rem !important;
                        }
                        .compact-calendar .fc-event-title {
                          display: none !important;
                        }
                        .compact-calendar .fc-daygrid-event-dot {
                          margin: 0 1px !important;
                        }
                        .compact-calendar .fc-daygrid-day-events {
                          margin-top: 0 !important;
                        }
                        .compact-calendar .fc-daygrid-day-frame {
                          min-height: 2.25rem !important;
                        }
                      }
                    `}</style>
                    <div
                      ref={calendarRef}
                      className="h-full compact-calendar"
                    ></div>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
                    <p>캘린더를 불러오는 중...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 급식 정보 카드 */}
          <div className="min-w-0 lg:col-span-4">
            <div className="fe-surface bg-white rounded-3xl border border-gray-200 shadow-lg p-4 sm:p-6 h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="fe-home-icon fe-home-icon--peach">
                  <UtensilsCrossed className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">급식정보</h3>
              </div>
              <div className="mb-4">
                <input
                  type="date"
                  aria-label="급식 날짜"
                  value={mealDate}
                  onChange={handleMealDateChange}
                  className="fe-input fe-meal-date px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {mealInfo && mealInfo.mealTypes.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {mealInfo.mealTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => handleMealTypeClick(type)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedMealType === type
                        ? 'bg-[#35402e] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
              {mealInfo && selectedMealType && mealInfo.meals[selectedMealType] ? (
                <div className="bg-[#f3f5ed] rounded-2xl p-6 min-h-[200px] flex content-start flex-wrap gap-2.5">
                  {(Array.isArray(mealInfo.meals[selectedMealType])
                    ? (mealInfo.meals[selectedMealType] as string[])
                    : [String(mealInfo.meals[selectedMealType])]
                  ).flatMap(text =>
                    String(text)
                      .split(/&lt;br\s*\/?\s*&gt;|<br\s*\/?\s*>|\n/i)
                      .map(item => item.trim())
                      .filter(item => item.length > 0)
                  ).map((item, index) => (
                    <span key={index} className="inline-flex items-center px-3.5 py-2 rounded-full bg-white border border-gray-200 shadow-sm text-sm font-medium text-gray-700 hover:border-green-300 hover:bg-green-50 hover:text-green-800 transition-colors cursor-default">
                      {item}
                    </span>
                  ))}
                </div>
              ) : mealInfo && mealInfo.mealTypes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>해당 날짜의 급식 정보가 없습니다.</p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>급식 정보를 불러오는 중...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedScheduleDay && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-day-title"
          onClick={() => setSelectedScheduleDay(null)}
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
              <div className="min-w-0">
                <p className="text-xs font-medium text-purple-600 mb-1">학사일정</p>
                <h4 id="schedule-day-title" className="text-lg font-bold text-gray-900">
                  {formatScheduleDayLabel(selectedScheduleDay.date)}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => setSelectedScheduleDay(null)}
                className="shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                aria-label="닫기"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              {selectedScheduleDay.events.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">이 날의 학사일정이 없습니다.</p>
              ) : (
                <ul className="space-y-2">
                  {selectedScheduleDay.events.map((ev, index) => (
                    <li
                      key={`${ev.start}-${ev.title}-${index}`}
                      className="flex items-start gap-3 rounded-xl bg-gray-50 border border-gray-100 px-3.5 py-3"
                    >
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" aria-hidden />
                      <span className="text-sm font-medium text-gray-800 leading-relaxed">{ev.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-5 pb-5 pt-1 sm:pb-4">
              <button
                type="button"
                onClick={() => setSelectedScheduleDay(null)}
                className="w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function Dashboard() {
  const [teacherCount, setTeacherCount] = useState<number | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadTeacherCount = async () => {
      if (!supabase) return;

      const { count, error } = await supabase
        .from("auth_user")
        .select("id", { count: "exact", head: true });

      if (error || isCancelled || typeof count !== "number") return;
      setTeacherCount(count);
    };

    void loadTeacherCount();

    return () => {
      isCancelled = true;
    };
  }, []);


  usePageSEO({
    title: "Foreducator | 생기부·학급관리·시간표·교사 커뮤니티",
    description:
      "교사를 위한 올인원 플랫폼. 생기부 문구·평가계획·서류 수합, 자리배치·출석·수업 퀴즈, 학교 시간표 자동 생성, 교사 커뮤니티까지 한곳에서.",
    path: "/",
    keywords:
      "교사, 생기부, 학급관리, 자리배치, 출석부, 평가계획, 시간표 프로그램, 교사 커뮤니티, 수업 퀴즈, Foreducator",
  });

  return (
    <div className="fe-home">
      <HomeHero teacherCount={teacherCount} />
      {/* 나만의 바로가기 즐겨찾기 섹션 */}
      <QuickFavorites />

      {/* 학교 정보 히어로 섹션 */}
      <SchoolInfoHero />

      {/* 커뮤니티 하이라이트 섹션 */}
      <CommunityHighlightsSection />

      <section className="fe-home-outro">
        <span className="fe-eyebrow">MADE FOR YOUR EVERYDAY</span>
        <h2>좋은 교육에 집중할 수 있도록.</h2>
        <p>선생님의 작은 아이디어가 더 나은 교실을 만듭니다.</p>
        <Link to="/community" className="fe-button fe-button-dark">선생님들과 이야기 나누기 <ArrowRight size={18} /></Link>
      </section>
    </div>
  );
}
